import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type ReactNode,
} from 'react';
import { Icon, type IconName } from './icons';
import { tr } from '../i18n';
import { applyStoredThemeFull, persistTheme, readStoredTheme, resolveTheme } from '../lib/theme';

/**
 * Small UI primitives shared by every screen. Styling lives in index.css (tokens and the
 * .btn / .field / .badge / .dialog classes); this file adds the behaviour that CSS cannot:
 * focus trapping, live regions, confirm prompts, retryable error states.
 */

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

/**
 * Moves focus back to the barcode box. Called after every dialog closes on the till — unless
 * another dialog has opened in its place (the search palette handing over to the customer
 * dialog), which keeps the focus it took.
 */
export function focusBarcode(): void {
  window.requestAnimationFrame(() => {
    if (document.querySelector('[aria-modal="true"]')) return;
    const input = document.querySelector<HTMLInputElement>('[data-testid="pos-barcode-input"]');
    if (input && !input.disabled) input.focus();
  });
}

// ---------------------------------------------------------------- atoms

export function Spinner({ label = tr('common.loading') }: { label?: string }) {
  return <span className="spinner" role="status" aria-label={label} />;
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('skeleton', className)} aria-hidden />;
}

/** Placeholder rows for a table that is still loading. */
export function SkeletonRows({ rows = 6 }: { rows?: number }) {
  return (
    <div className="space-y-2 p-3" role="status" aria-label={tr('common.loading')}>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-9 w-full" />
      ))}
    </div>
  );
}

type Tone = 'neutral' | 'success' | 'warn' | 'danger' | 'info' | 'primary';

const BADGE_TONES: Record<Tone, string> = {
  neutral: '',
  success: 'badge-success',
  warn: 'badge-warn',
  danger: 'badge-danger',
  info: 'badge-info',
  primary: 'badge-primary',
};

export function Badge({ tone = 'neutral', children, className }: { tone?: Tone; children: ReactNode; className?: string }) {
  return <span className={cx('badge', BADGE_TONES[tone], className)}>{children}</span>;
}

export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return <kbd className={cx('kbd', className)}>{children}</kbd>;
}

export function Card({
  title,
  actions,
  children,
  className,
}: {
  title?: string;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx('panel', className)}>
      {title || actions ? (
        <header className="panel-head">
          {title ? <h2>{title}</h2> : <span />}
          {actions}
        </header>
      ) : null}
      <div className="p-4">{children}</div>
    </section>
  );
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'primary' | 'danger' | 'ghost' | 'success';
  size?: 'sm' | 'md' | 'lg';
  busy?: boolean;
}

export function Button({ variant = 'default', size = 'md', busy = false, className, children, disabled, type, ...rest }: ButtonProps) {
  return (
    <button
      type={type ?? 'button'}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      className={cx(
        'btn',
        variant === 'primary' && 'btn-primary',
        variant === 'danger' && 'btn-danger',
        variant === 'ghost' && 'btn-ghost',
        variant === 'success' && 'btn-success',
        size === 'sm' && 'btn-sm',
        size === 'lg' && 'btn-lg',
        className,
      )}
      {...rest}
    >
      {busy ? <span className="spinner" aria-hidden /> : null}
      {children}
    </button>
  );
}

/** Label + control + hint + inline error, wired together with ids so screen readers read them as one. */
export function Field({
  label,
  hint,
  error,
  children,
  className,
}: {
  label: string;
  hint?: string;
  error?: string | null;
  children: (props: { id: string; 'aria-describedby': string | undefined; 'aria-invalid': boolean | undefined }) => ReactNode;
  className?: string;
}) {
  const id = useId();
  const noteId = `${id}-note`;
  const describedBy = error || hint ? noteId : undefined;
  return (
    <div className={className}>
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      {children({ id, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined })}
      {error ? (
        <p id={noteId} className="field-error" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={noteId} className="field-hint">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

export function EmptyState({
  title,
  message,
  action,
  icon = 'inbox',
}: {
  title: string;
  message?: string;
  action?: ReactNode;
  icon?: IconName;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 px-6 py-12 text-center">
      <span
        className="flex h-14 w-14 items-center justify-center rounded-full"
        style={{ background: 'var(--ui-primary-soft)', color: 'var(--ui-primary)' }}
        aria-hidden
      >
        <Icon name={icon} size={26} />
      </span>
      <p className="mt-2 text-base font-semibold text-slate-800">{title}</p>
      {message ? <p className="max-w-sm text-sm text-slate-500">{message}</p> : null}
      {action ? <div className="mt-3">{action}</div> : null}
    </div>
  );
}

export function ErrorState({
  title = 'Something went wrong',
  message,
  onRetry,
}: {
  title?: string;
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <div role="alert" className="flex flex-col items-center justify-center gap-1 px-6 py-10 text-center">
      <span
        className="flex h-14 w-14 items-center justify-center rounded-full"
        style={{ background: 'var(--ui-danger-soft)', color: 'var(--ui-danger)' }}
        aria-hidden
      >
        <Icon name="alert" size={26} />
      </span>
      <p className="mt-2 text-base font-semibold" style={{ color: 'var(--ui-danger)' }}>
        {title}
      </p>
      {message ? <p className="max-w-md text-sm text-slate-600">{message}</p> : null}
      {onRetry ? (
        <Button className="mt-3" onClick={onRetry}>
          {tr('common.tryAgain')}
        </Button>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- modal

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface ModalProps {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Tailwind max-width class, e.g. "max-w-md". */
  size?: string;
  /** Extra header controls shown before the Close button. */
  headerActions?: ReactNode;
  /** Hide the Close button (when the footer already has the single way out). */
  hideClose?: boolean;
  testId?: string;
  /** Disable Esc / backdrop dismissal while a request is in flight. */
  locked?: boolean;
  /** Where focus goes when the dialog closes. Defaults to the element focused before it opened. */
  restoreFocus?: 'previous' | 'barcode' | 'none';
  closeLabel?: string;
  /** "sheet" slides in from the right edge at full height, beside the bill it is about. */
  placement?: 'center' | 'sheet';
  /** For a dialog that is its own header (the search palette): the title stays, for screen readers only. */
  hideHeader?: boolean;
  /** Extra classes on the scrim, e.g. to dock the dialog near the top of the screen. */
  scrimClassName?: string;
}

/**
 * Accessible dialog: role=dialog + aria-modal, labelled by its title, traps Tab inside,
 * closes on Esc, and hands focus back where it came from (the barcode box on the till).
 */
export function Modal({
  title,
  subtitle,
  onClose,
  children,
  footer,
  size = 'max-w-md',
  headerActions,
  hideClose = false,
  testId,
  locked = false,
  restoreFocus = 'previous',
  closeLabel = tr('common.close'),
  placement = 'center',
  hideHeader = false,
  scrimClassName,
}: ModalProps) {
  const titleId = useId();
  const panel = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const lockedRef = useRef(locked);
  lockedRef.current = locked;

  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const node = panel.current;

    // Focus the first control that asked for it, else the first field, else the panel itself.
    const initial =
      node?.querySelector<HTMLElement>('[autofocus], [data-autofocus]') ??
      node?.querySelector<HTMLElement>('input:not([disabled]), select:not([disabled]), textarea:not([disabled])') ??
      node?.querySelector<HTMLElement>('button:not([data-close])') ??
      node;
    initial?.focus();

    function onKey(event: KeyboardEvent) {
      const dialogs = document.querySelectorAll('[aria-modal="true"]');
      if (dialogs.item(dialogs.length - 1) !== node) return;
      // IME composition (Tamil) cancels with Esc inside the field: never close the dialog.
      if (event.isComposing || event.keyCode === 229) return;
      if (event.key === 'Escape') {
        event.stopPropagation();
        if (!lockedRef.current) closeRef.current();
        return;
      }
      if (event.key !== 'Tab' || !node) return;
      const items = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (items.length === 0) {
        event.preventDefault();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !node.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !node.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      if (restoreFocus === 'barcode') focusBarcode();
      else if (restoreFocus === 'previous' && previous && document.contains(previous)) {
        window.requestAnimationFrame(() => previous.focus());
      } else if (restoreFocus === 'previous') focusBarcode();
    };
  }, [restoreFocus]);

  return (
    <div
      className={cx('scrim', placement === 'sheet' && 'scrim-sheet', scrimClassName)}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !locked) onClose();
      }}
    >
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cx('dialog', placement === 'sheet' ? 'dialog-sheet' : size)}
        data-testid={testId}
      >
        {hideHeader ? (
          <h2 id={titleId} className="sr-only">
            {title}
          </h2>
        ) : (
          <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-3 sm:px-5 sm:py-3.5">
            <div className="min-w-0">
              <h2 id={titleId} className="text-lg leading-snug font-semibold text-slate-900">
                {title}
              </h2>
              {subtitle ? <p className="text-sm text-slate-500">{subtitle}</p> : null}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {headerActions}
              {hideClose ? null : (
                <button type="button" data-close className="btn btn-sm" onClick={onClose} disabled={locked}>
                  {closeLabel}
                  <span className="sr-only"> dialog</span>
                </button>
              )}
            </div>
          </header>
        )}
        <div className={cx('min-h-0 flex-1 overflow-y-auto', hideHeader ? '' : 'px-4 py-4 sm:px-5')}>{children}</div>
        {footer ? <footer className="border-t border-slate-200 bg-slate-50 px-4 py-3 sm:px-5">{footer}</footer> : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- toasts and confirm

type ToastTone = 'good' | 'warn' | 'error' | 'info';
interface ToastItem {
  id: number;
  tone: ToastTone;
  message: string;
}

interface Feedback {
  toast: (tone: ToastTone, message: string) => void;
  confirm: (options: ConfirmOptions) => Promise<boolean>;
}

export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

const FeedbackContext = createContext<Feedback | null>(null);
let toastId = 0;

const TOAST_ICON: Record<ToastTone, string> = { good: '✓', warn: '!', error: '✕', info: 'i' };

/**
 * Hosts the toast stack (an aria-live region, so scans and errors are announced) and the
 * promise based confirm dialog used for destructive actions.
 */
export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [pending, setPending] = useState<{ options: ConfirmOptions; resolve: (value: boolean) => void } | null>(null);

  const dismiss = useCallback((id: number) => setToasts((current) => current.filter((item) => item.id !== id)), []);

  const toast = useCallback(
    (tone: ToastTone, message: string) => {
      const id = ++toastId;
      // A repeated message replaces its twin rather than stacking five "Added Tata Salt" toasts.
      setToasts((current) => [...current.filter((item) => item.message !== message).slice(-2), { id, tone, message }]);
      // Confirmations leave quickly; warnings get long enough to be read, and errors clear after
      // five seconds — a failed scan's message must be gone before the next customer steps up.
      // All of them can still be dismissed by hand; nothing lingers on an unrelated screen.
      const lifetime = tone === 'good' ? 2200 : tone === 'info' ? 3000 : tone === 'warn' ? 9000 : 5000;
      window.setTimeout(() => dismiss(id), lifetime);
    },
    [dismiss],
  );

  const confirm = useCallback(
    (options: ConfirmOptions) => new Promise<boolean>((resolve) => setPending({ options, resolve })),
    [],
  );

  const value = useMemo(() => ({ toast, confirm }), [toast, confirm]);

  function settle(answer: boolean) {
    pending?.resolve(answer);
    setPending(null);
  }

  return (
    <FeedbackContext.Provider value={value}>
      {children}

      <div className="toast-stack no-print">
        {toasts.map((item) => (
          <div
            key={item.id}
            role={item.tone === 'error' || item.tone === 'warn' ? 'alert' : 'status'}
            aria-live={item.tone === 'error' || item.tone === 'warn' ? 'assertive' : 'polite'}
            className={`toast toast-${item.tone}`}
            data-testid={`toast-${item.tone}`}
          >
            <span aria-hidden className="mt-px inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/25 text-xs">
              {TOAST_ICON[item.tone]}
            </span>
            <span className="min-w-0 flex-1 break-words">{item.message}</span>
            {item.tone === 'error' || item.tone === 'warn' ? (
              <button
                type="button"
                aria-label={tr('common.dismiss')}
                className="toast-dismiss -my-1 -mr-1.5 rounded px-2 py-1 text-base leading-none hover:bg-white/20"
                onClick={() => dismiss(item.id)}
              >
                ×
              </button>
            ) : null}
          </div>
        ))}
      </div>

      {pending ? (
        <Modal
          title={pending.options.title}
          onClose={() => settle(false)}
          hideClose
          size="max-w-sm"
          testId="confirm-dialog"
          footer={
            <div className="flex justify-end gap-2">
              <Button onClick={() => settle(false)} data-testid="confirm-cancel">
                {pending.options.cancelLabel ?? tr('common.cancel')}
              </Button>
              <Button
                variant={pending.options.danger ? 'danger' : 'primary'}
                onClick={() => settle(true)}
                data-testid="confirm-ok"
              >
                {pending.options.confirmLabel ?? tr('common.confirm')}
              </Button>
            </div>
          }
        >
          {pending.options.message ? <div className="text-sm text-slate-600">{pending.options.message}</div> : null}
        </Modal>
      ) : null}
    </FeedbackContext.Provider>
  );
}

export function useFeedback(): Feedback {
  const value = useContext(FeedbackContext);
  if (!value) throw new Error('useFeedback needs a <FeedbackProvider>.');
  return value;
}

// ---------------------------------------------------------------- theme

type ThemeChoice = 'system' | 'light' | 'dark';
const THEME_KEY = 'sp.theme';

/** Blocked storage must read as "no preference", never throw. */
function themeStore(): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> {
  try {
    void localStorage.length;
    return localStorage;
  } catch {
    const memory = new Map<string, string>();
    return {
      getItem: (key: string) => memory.get(key) ?? null,
      setItem: (key: string, value: string) => {
        memory.set(key, value);
      },
      removeItem: (key: string) => {
        memory.delete(key);
      },
    };
  }
}

function readTheme(): ThemeChoice {
  // Single authority lives in lib/theme; this keeps the legacy switch working on top of it.
  return readStoredTheme(themeStore()).appearance ?? 'system';
}

export function applyStoredTheme(): void {
  applyStoredThemeFull(themeStore(), document.documentElement);
}

export function ThemeSwitch({ className }: { className?: string }) {
  const [choice, setChoice] = useState<ThemeChoice>(readTheme);
  const next: Record<ThemeChoice, ThemeChoice> = { system: 'light', light: 'dark', dark: 'system' };
  const icon: Record<ThemeChoice, IconName> = { system: 'monitor', light: 'sun', dark: 'moon' };
  const name = tr(choice === 'system' ? 'theme.auto' : choice === 'light' ? 'theme.light' : 'theme.dark');
  return (
    <button
      type="button"
      className={className}
      aria-label={tr('theme.choice', { choice: name })}
      title={tr('theme.title', { choice: name })}
      onClick={() => {
        const value = next[choice];
        setChoice(value);
        const store = themeStore();
        try {
          if (value === 'system') store.removeItem(THEME_KEY);
          else store.setItem(THEME_KEY, value);
        } catch {
          // storage blocked: the choice still applies for this session
        }
        // The separated keys are the authority; the legacy key above stays as a
        // readable fallback for anything that still looks at sp.theme.
        persistTheme(store, { ...resolveTheme({ stored: readStoredTheme(store) }), appearance: value });
        applyStoredTheme();
      }}
    >
      <Icon name={icon[choice]} size={16} />
      <span>{name}</span>
    </button>
  );
}

// ---------------------------------------------------------------- online

export function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

// ---------------------------------------------------------------- layout

/**
 * Whether a CSS media query matches, kept live. For screens that need a different STRUCTURE on a
 * phone (cards instead of a wide entry table), where hiding one of two copies with CSS would put
 * every input and test id on the page twice.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const list = window.matchMedia(query);
    const update = () => setMatches(list.matches);
    update();
    list.addEventListener('change', update);
    return () => list.removeEventListener('change', update);
  }, [query]);
  return matches;
}
